import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { IndianRupee, ShoppingBag, TrendingUp } from 'lucide-react';

const COLORS = ['hsl(100, 23%, 33%)', 'hsl(75, 35%, 55%)', 'hsl(40, 50%, 55%)', 'hsl(140, 30%, 45%)', 'hsl(60, 40%, 50%)'];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/analytics').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-full p-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const statusData = Object.entries(data.order_status_distribution || {}).map(([name, value]) => ({ name, value }));

  return (
    <div data-testid="admin-analytics-page" className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-serif font-medium">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Insights and trends for your marketplace</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card className="rounded-xl">
          <CardContent className="pt-6 text-center">
            <IndianRupee className="h-8 w-8 mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-semibold">{'\u20B9'}{data.total_revenue?.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Total Revenue</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6 text-center">
            <ShoppingBag className="h-8 w-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-semibold">{data.total_orders}</p>
            <p className="text-xs text-muted-foreground">Total Orders</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6 text-center">
            <TrendingUp className="h-8 w-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-semibold">{data.top_products?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Active Products</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card className="rounded-xl">
          <CardHeader><CardTitle className="text-base font-medium">Top Products by Revenue</CardTitle></CardHeader>
          <CardContent>
            {data.top_products?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.top_products}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(85,24%,85%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [`\u20B9${v}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="hsl(100, 23%, 33%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data yet</p>}
          </CardContent>
        </Card>

        {/* Order Status */}
        <Card className="rounded-xl">
          <CardHeader><CardTitle className="text-base font-medium">Order Status Distribution</CardTitle></CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                    {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data yet</p>}
            <div className="flex flex-wrap gap-3 justify-center mt-4">
              {statusData.map((s, i) => (
                <div key={s.name} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="capitalize">{s.name}: {s.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Farmer Performance */}
        <Card className="rounded-xl lg:col-span-2">
          <CardHeader><CardTitle className="text-base font-medium">Farmer Performance</CardTitle></CardHeader>
          <CardContent>
            {data.farmer_performance?.length > 0 ? (
              <div className="space-y-3">
                {data.farmer_performance.map((f, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-sm">{f.name}</p>
                      <p className="text-xs text-muted-foreground">{f.orders} orders</p>
                    </div>
                    <span className="font-semibold">{'\u20B9'}{f.revenue?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data yet</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
